
import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiUsers, FiHeart, FiGlobe, FiCoffee, FiStar, FiArrowRight} = FiIcons;

const Careers = () => {
  const positions = [
    {
      id: 1,
      title: "Senior Technical Recruiter",
      type: "Full-time",
      location: "Remote (Germany)",
      department: "Recruitment",
      description: "Join our team to help scale innovative robotics and AI companies by finding exceptional talent."
    },
    {
      id: 2,
      title: "Talent Partner",
      type: "Full-time",
      location: "Berlin, Germany",
      department: "Recruitment",
      description: "Work directly with deep tech startups to understand their hiring needs and build high-performing teams."
    },
    {
      id: 3,
      title: "HR Consultant",
      type: "Contract",
      location: "Remote (Germany)",
      department: "HR Advisory",
      description: "Provide strategic HR guidance to help scaling tech companies build strong organizational foundations."
    }
  ];

  const benefits = [
    {
      icon: FiHeart,
      title: "Health & Wellbeing",
      description: "Comprehensive health insurance and wellness programs to keep you at your best."
    },
    {
      icon: FiGlobe,
      title: "Remote-First",
      description: "Work from anywhere in Germany with flexible hours and regular team meetups."
    },
    {
      icon: FiCoffee,
      title: "Learning Budget",
      description: "€1,000 annual budget for professional development and learning resources."
    },
    {
      icon: FiUsers,
      title: "Team Events",
      description: "Regular team socials, offsites, and opportunities to connect with colleagues."
    },
    {
      icon: FiStar,
      title: "Performance Bonus",
      description: "Quarterly bonus scheme based on individual and company performance."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Minimalist */}
      <section className="relative bg-gray-900 py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Join Our Mission
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
              Help us build the future of tech recruitment. We're looking for passionate people to join our team.
            </p>
            <a
              href="#positions"
              className="inline-flex items-center bg-brand-orange text-white px-8 py-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors duration-200"
            >
              View Open Positions
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Culture Section - Ultra clean */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Life at Trbe
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're building a culture where innovation, collaboration, and personal growth thrive.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Remote-First Culture",
                description: "Work from anywhere in Germany. We believe great work can happen anywhere.",
                image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800"
              },
              {
                title: "Growth & Development",
                description: "Regular learning sessions, mentorship, and career development opportunities.",
                image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=800"
              },
              {
                title: "Work That Matters",
                description: "Help innovative companies build teams that shape the future of technology.",
                image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section - Light and airy */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Benefits & Perks
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer competitive compensation and benefits to help you do your best work.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={benefit.icon} className="h-6 w-6 text-brand-purple" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section - Clean and minimal */}
      <section id="positions" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our team and help build the future of tech recruitment.
            </p>
          </motion.div>
          <div className="space-y-4">
            {positions.map((position, index) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {position.title}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4 md:mb-0">
                      <span>{position.type}</span>
                      <span>•</span>
                      <span>{position.location}</span>
                      <span>•</span>
                      <span>{position.department}</span>
                    </div>
                  </div>
                  <a
                    href="mailto:apply@trbe.ai"
                    className="inline-flex items-center bg-brand-orange text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors duration-200"
                  >
                    Apply Now
                    <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
                  </a>
                </div>
                <p className="mt-4 text-gray-600">
                  {position.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Simple contrast */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Don't See the Right Role?
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
              We're always looking for talented people to join our team. Send us your CV and we'll keep you in mind for future opportunities.
            </p>
            <a
              href="mailto:apply@trbe.ai"
              className="inline-flex items-center bg-brand-orange text-white px-8 py-4 rounded-lg font-medium hover:bg-opacity-90 transition-colors duration-200"
            >
              Send Your CV
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
